import React from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Box, Typography, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const GeographicChart = ({ data, title = "Análise Geográfica Consolidada" }) => {
  // Top 15 estados por volume de vendas
  const top15States = [...data]
    .sort((a, b) => b.total_sales - a.total_sales)
    .slice(0, 15);

  // Dados para gráfico de barras - Volume de Vendas
  const volumeChartData = {
    labels: top15States.map(item => item.state_code),
    datasets: [
      {
        label: 'Volume de Vendas',
        data: top15States.map(item => item.total_sales),
        backgroundColor: 'rgba(69, 179, 209, 0.8)',
        borderColor: 'rgba(69, 179, 209, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Top 15 estados por receita total
  const top15Revenue = [...data]
    .sort((a, b) => parseFloat(b.total_revenue) - parseFloat(a.total_revenue))
    .slice(0, 15);

  // Dados para gráfico de barras - Receita Total
  const revenueChartData = {
    labels: top15Revenue.map(item => item.state_code),
    datasets: [
      {
        label: 'Receita Total (R$)',
        data: top15Revenue.map(item => parseFloat(item.total_revenue)),
        backgroundColor: 'rgba(150, 206, 180, 0.8)',
        borderColor: 'rgba(150, 206, 180, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Top 10 estados por ticket médio
  const top10Ticket = [...data]
    .sort((a, b) => parseFloat(b.average_ticket) - parseFloat(a.average_ticket))
    .slice(0, 10);

  // Dados para gráfico de barras - Ticket Médio
  const ticketChartData = {
    labels: top10Ticket.map(item => item.state_code),
    datasets: [
      {
        label: 'Ticket Médio (R$)',
        data: top10Ticket.map(item => parseFloat(item.average_ticket)),
        backgroundColor: 'rgba(255, 152, 116, 0.8)',
        borderColor: 'rgba(255, 152, 116, 1)',
        borderWidth: 1,
      },
    ],
  };

  // Dados para gráfico de pizza - Participação por Região
  const regionData = data.reduce((acc, item) => {
    const region = item.region || 'Outros';
    if (!acc[region]) {
      acc[region] = { total_sales: 0, total_revenue: 0 };
    }
    acc[region].total_sales += item.total_sales;
    acc[region].total_revenue += parseFloat(item.total_revenue);
    return acc;
  }, {});

  const pieChartData = {
    labels: Object.keys(regionData),
    datasets: [
      {
        data: Object.values(regionData).map(item => item.total_sales),
        backgroundColor: [
          '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3'
        ],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.parsed?.y || context.parsed || 0;
            if (context.dataset?.label?.includes('Receita') || context.dataset?.label?.includes('Ticket')) {
              return `${context.dataset.label}: R$ ${value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
            }
            return `${context.dataset.label}: ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Estados',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Valores',
        },
        ticks: {
          callback: function(value) {
            try {
              return value.toLocaleString('pt-BR');
            } catch (e) {
              return value;
            }
          },
        },
      },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed.toLocaleString('pt-BR')} (${percentage}%)`;
          },
        },
      },
    },
  };

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={300}>
            <Typography color="text.secondary">
              Não há dados disponíveis para exibir
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        {title}
      </Typography>
      
      <Grid container spacing={3}>
        {/* Gráfico de Volume de Vendas */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top 15 Estados - Volume de Vendas
              </Typography>
              <Box height={320}>
                <Bar data={volumeChartData} options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      title: { display: true, text: 'Volume de Vendas' }
                    }
                  }
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gráfico de Receita Total */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top 15 Estados - Receita Total (R$)
              </Typography>
              <Box height={320}>
                <Bar data={revenueChartData} options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      title: { display: true, text: 'Receita Total (R$)' }
                    }
                  }
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gráfico de Ticket Médio */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top 10 Estados - Ticket Médio (R$)
              </Typography>
              <Box height={320}>
                <Bar data={ticketChartData} options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      title: { display: true, text: 'Ticket Médio (R$)' }
                    }
                  }
                }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gráfico de Pizza - Participação por Região */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: 400 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Participação por Região Brasileira
              </Typography>
              <Box height={320}>
                <Pie data={pieChartData} options={pieOptions} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabela de Métricas por Estado (Top 15) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Métricas Financeiras por Estado (TOP 15)
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Estado</strong></TableCell>
                      <TableCell align="right"><strong>Clientes</strong></TableCell>
                      <TableCell align="right"><strong>R$ Ticket Médio</strong></TableCell>
                      <TableCell align="right"><strong>R$ Menor Compra</strong></TableCell>
                      <TableCell align="right"><strong>R$ Maior Compra</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top15Revenue.map((row) => {
                      const minPurchase = parseFloat(row.average_ticket) * 0.1; // Simulação
                      const maxPurchase = parseFloat(row.average_ticket) * 15; // Simulação
                      
                      return (
                        <TableRow key={row.state_code} hover>
                          <TableCell component="th" scope="row">
                            <strong>{row.state_code}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {row.total_sales.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell align="right">
                            R$ {parseFloat(row.average_ticket).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                          <TableCell align="right">
                            R$ {minPurchase.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                          <TableCell align="right">
                            R$ {maxPurchase.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default GeographicChart;
